/**
 * SOFSEK — Operational Bottleneck Dashboard (§3). Capabilities held by exactly
 * one holder across the whole matrix. "Run Scan" calls sfk_run_spof_scan(),
 * which upserts active findings and auto-resolves ones that no longer qualify
 * (never deletes — history is the product, same pattern as violations, §37).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { useAdaptiveList } from '../../lib/use_adaptive_list';
import { hasAccess, type AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search, Play } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.SPOF_FINDING.LIST';

interface CodeOpt { code: string; name: string; }
interface FindingRow {
  finding_id: number;
  application_id: number;
  permission_id: number;
  sole_holder_app_user_id: number | null;
  sole_holder_login_user_id: number | null;
  continuity_status_code: string;
  is_active: 'Y' | 'N';
  last_scanned_at: string;
}

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'permission', header: 'Capability / Screen', defaultWidth: 240 },
  { key: 'application', header: 'Application', defaultWidth: 180 },
  { key: 'holder', header: 'Sole Holder', defaultWidth: 200 },
  { key: 'continuity_status', header: 'Continuity Status', defaultWidth: 180 },
  { key: 'last_scanned_at', header: 'Last Scanned', defaultWidth: 170 },
];

function SpofInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [continuityStatuses, setContinuityStatuses] = useState<CodeOpt[]>([]);
  const [permissions, setPermissions] = useState<Map<number, string>>(new Map());
  const [subjects, setSubjects] = useState<Map<number, string>>(new Map());
  const [users, setUsers] = useState<Map<number, string>>(new Map());
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [appFacet, setAppFacet] = useState<string[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [scanning, setScanning] = useState(false);
  // §6/§17: this page has no routed* write, so the affordance gate never
  // flagged it — but sfk_run_spof_scan inserts findings, so it is a write.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  // Small code lookups load once (apps + continuity statuses).
  const loadLookups = async () => {
    const [appRes, statusRes] = await Promise.all([
      apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
      apiDb.from('sfk_continuity_status').select('continuity_status_code, sfk_continuity_status_tl ( locale_code, display_text )'),
    ]);
    const err = appRes.error ?? statusRes.error;
    if (err) { setLookupError(err.message); return; }
    setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)) })));
    setContinuityStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.continuity_status_code as string, name: tlName(r.sfk_continuity_status_tl, r.continuity_status_code as string) })));
  };
  useEffect(() => { void loadLookups(); }, []);

  // Adaptive main list — facets pushed server-side; text search (over resolved
  // permission/holder names, which are not base-table columns) stays client-side
  // over the loaded page.
  const mapRow = useCallback((r: Record<string, unknown>) => r as unknown as FindingRow, []);
  const facets = useMemo(() => [
    { column: 'application_id', values: appFacet.map(Number) },
    { column: 'continuity_status_code', values: statusFacet },
    { column: 'is_active', values: activeFacet },
  ], [appFacet, statusFacet, activeFacet]);
  const { rows, total, truncated, busy, error: listError, reload } =
    useAdaptiveList<Record<string, unknown>, FindingRow>({
      table: 'sfk_spof_finding',
      columns: 'finding_id, application_id, permission_id, sole_holder_app_user_id, sole_holder_login_user_id, continuity_status_code, is_active, last_scanned_at',
      order: { column: 'last_scanned_at', ascending: false },
      search: '',
      searchColumns: [],
      facets,
      map: mapRow,
    });
  const error = listError ?? lookupError;

  // Resolve only the page's referenced ids (scoped .in()), never a full preload of
  // the ~7,800-row action catalog / user tables.
  useEffect(() => {
    const permIds = [...new Set(rows.map(r => r.permission_id).filter((v): v is number => v != null))];
    const subjIds = [...new Set(rows.map(r => r.sole_holder_app_user_id).filter((v): v is number => v != null))];
    const userIds = [...new Set(rows.map(r => r.sole_holder_login_user_id).filter((v): v is number => v != null))];
    let cancelled = false;
    void (async () => {
      if (permIds.length) {
        const res = await apiDb.from('sfk_v_action').select('permission_id, display_name').in('permission_id', permIds);
        if (!cancelled && !res.error) setPermissions(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.permission_id as number, r.display_name as string])));
      }
      if (subjIds.length) {
        const res = await apiDb.from('sfk_subject_profile').select('user_id, csm_user_master!fk_sfk_subject_profile_user ( display_name )').in('user_id', subjIds);
        if (!cancelled && !res.error) setSubjects(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.user_id as number, ((r.csm_user_master ?? {}) as { display_name?: string }).display_name ?? String(r.user_id)])));
      }
      if (userIds.length) {
        const res = await apiDb.from('csm_user_master').select('user_id, display_name').in('user_id', userIds);
        if (!cancelled && !res.error) setUsers(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.user_id as number, r.display_name as string])));
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const runScan = async () => {
    setScanning(true); setLookupError(null);
    try {
      const { error: e } = await apiDb.rpc('sfk_run_spof_scan');
      if (e) throw e;
      reload();
    } catch (e) { setLookupError((e as Error).message); } finally { setScanning(false); }
  };

  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const permName = (id: number) => permissions.get(id) ?? String(id);
  const holderName = (r: FindingRow) =>
    r.sole_holder_app_user_id != null ? (subjects.get(r.sole_holder_app_user_id) ?? String(r.sole_holder_app_user_id))
    : r.sole_holder_login_user_id != null ? `${t('sfk.spof.self_operator', 'SOFSEK operator')}: ${users.get(r.sole_holder_login_user_id) ?? String(r.sole_holder_login_user_id)}`
    : '—';
  const resolveNameForStatus = (code: string) => continuityStatuses.find(s => s.code === code)?.name ?? code;

  const detail = useExpandedRows<number>();
  // §17.B13 — related records for a SPOF finding: the application + capability it
  // covers and the sole holder who bottlenecks it (resolved names, §17.B4).
  const buildRelatedGroups = (r: FindingRow): RelatedChildGroup[] => [
    { key: 'capability', title: t('sfk.spof.related.capability', 'Capability'),
      items: [{ id: r.permission_id, label: permName(r.permission_id), title: appName(r.application_id) }] },
    { key: 'holder', title: t('sfk.spof.related.holder', 'Sole holder'),
      items: (r.sole_holder_app_user_id != null || r.sole_holder_login_user_id != null) ? [{ id: 'holder', label: holderName(r) }] : [] },
  ];
  const hasRelated = (r: FindingRow) => buildRelatedGroups(r).some(g => g.items.length > 0);

  // Facets are applied server-side by the hook; only the text search (over
  // resolved permission/holder names) is client-side, over the loaded page.
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => permName(r.permission_id).toLowerCase().includes(q) || holderName(r).toLowerCase().includes(q));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, permissions, subjects, users]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.spof.title', 'Operational Bottleneck Dashboard')}
          description={t('sfk.spof.description', 'Capabilities held by exactly one holder across the whole access matrix.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => void runScan()} disabled={scanning} className="btn-primary flex items-center gap-1.5 disabled:opacity-60"><Play className="h-3.5 w-3.5" /> {scanning ? t('sfk.spof.scanning', 'Scanning…') : t('sfk.spof.run_scan', 'Run Scan')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: total, label: t('sfk.spof.stat.total', 'Findings') },
          { value: filtered.length, label: t('common.shown', 'Shown') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.spof.facet.app', 'Application')} options={apps.map(a => ({ value: a.code, label: a.name }))} selected={appFacet} onChange={setAppFacet} />
            <FacetFilter label={t('sfk.spof.facet.status', 'Continuity')} options={continuityStatuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <FacetFilter label={t('common.boolean.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]} selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || appFacet.length > 0 || statusFacet.length > 0 || activeFacet.length !== 1} onClear={() => { setFilter(''); setAppFacet([]); setStatusFacet([]); setActiveFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {truncated && !busy && (
                <span className="text-csm-xs text-csm-muted">{t('common.list.truncated', 'Showing first {{n}} of {{total}} — narrow with facets', { n: rows.length, total })}</span>
              )}
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.finding_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.finding_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.finding_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'permission' && permName(r.permission_id)}
                        {col.key === 'application' && appName(r.application_id)}
                        {col.key === 'holder' && holderName(r)}
                        {col.key === 'continuity_status' && (
                          <span className={`rounded px-1.5 py-0.5 text-csm-xs ${r.continuity_status_code === 'NO_BACKUP' ? 'bg-csm-danger-soft text-csm-danger' : r.continuity_status_code === 'BACKUP_SCHEDULED' ? 'bg-csm-warning-soft text-csm-warning' : 'bg-csm-success-soft text-csm-success'}`}>
                            {resolveNameForStatus(r.continuity_status_code)}
                          </span>
                        )}
                        {col.key === 'last_scanned_at' && formatDateTime(r.last_scanned_at)}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.finding_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

export function SfkSpofFindingPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <SpofInner session={session} />
    </PersonalizationProvider>
  );
}
